import { create } from "zustand";
import { persist } from "zustand/middleware";
import { articleService } from "@/services/articleService";
import {
  Article,
  CreateArticleDto,
  UpdateArticleDto,
  GetPaginationArticlesDto,
} from "@/types/article";
import { showToast } from "@/lib/toast";

interface ArticleState {
  // State
  articles: Article[];
  selectedArticle: Article | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  isLoading: boolean;
  isLoadingDetail: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: string | null;

  // Actions
  fetchArticles: (params?: GetPaginationArticlesDto) => Promise<void>;
  fetchArticleById: (id: string) => Promise<void>;
  createArticle: (data: FormData | CreateArticleDto) => Promise<void>;
  updateArticle: (
    id: string,
    data: FormData | UpdateArticleDto
  ) => Promise<void>;
  deleteArticle: (id: string) => Promise<void>;
  changeStatus: (id: string, status: string) => Promise<void>;
  toggleDisplay: (id: string) => Promise<void>;
  setSelectedArticle: (article: Article | null) => void;
  clearSelectedArticle: () => void;
  clearError: () => void;
  setCurrentPage: (page: number) => void;
}

export const useArticleStore = create<ArticleState>()(
  persist(
    (set, get) => ({
      // Initial state
      articles: [],
      selectedArticle: null,
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      },
      isLoading: false,
      isLoadingDetail: false,
      isCreating: false,
      isUpdating: false,
      isDeleting: false,
      error: null,

      // Fetch articles with pagination
      fetchArticles: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response: any = await articleService.getPaginationArticles(
            params
          );
          if (response.ok && response.data) {
            set({
              articles: response.data?.data?.data.articles,
              pagination: response.data?.data?.data.pagination,
              isLoading: false,
            });
          } else {
            set({
              error: response.data?.message || "Lỗi khi tải danh sách bài viết",
              isLoading: false,
            });
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi tải danh sách bài viết",
            isLoading: false,
          });
        }
      },

      // Fetch article by ID
      fetchArticleById: async (id: string) => {
        set({ isLoadingDetail: true, error: null });
        try {
          const response: any = await articleService.getArticleById(id);
          if (response.ok && response.data) {
            set({
              selectedArticle: response.data?.data?.data,
              isLoadingDetail: false,
            });
          } else {
            set({
              error: response.data?.message || "Lỗi khi tải bài viết",
              isLoadingDetail: false,
            });
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi tải bài viết",
            isLoadingDetail: false,
          });
        }
      },

      // Create article
      createArticle: async (data: FormData | CreateArticleDto) => {
        set({ isCreating: true, error: null });
        try {
          const response: any = await articleService.create(data);
          if (response.ok && response.data) {
            const { pagination } = get();
            await get().fetchArticles({
              page: pagination.page,
              limit: pagination.limit,
            });
            set({ isCreating: false });
            showToast.success("Tạo bài viết thành công");
          } else {
            set({
              error: response.data?.message || "Lỗi khi tạo bài viết",
              isCreating: false,
            });
            showToast.error(response.data?.message || "Lỗi khi tạo bài viết");
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi tạo bài viết",
            isCreating: false,
          });
          showToast.error(error.message || "Lỗi khi tạo bài viết");
        }
      },

      // Update article
      updateArticle: async (id: string, data: FormData | UpdateArticleDto) => {
        set({ isUpdating: true, error: null });
        try {
          const response: any = await articleService.updateArticle(id, data);
          if (response.ok && response.data) {
            const { pagination } = get();
            await get().fetchArticles({
              page: pagination.page,
              limit: pagination.limit,
            });
            set({ isUpdating: false });
            showToast.success("Cập nhật bài viết thành công");
          } else {
            set({
              error: response.data?.message || "Lỗi khi cập nhật bài viết",
              isUpdating: false,
            });
            showToast.error(response.data?.message || "Lỗi khi cập nhật bài viết");
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi cập nhật bài viết",
            isUpdating: false,
          });
          showToast.error(error.message || "Lỗi khi cập nhật bài viết");
        }
      },

      // Delete article
      deleteArticle: async (id: string) => {
        set({ isDeleting: true, error: null });
        try {
          const response: any = await articleService.deleteArticle(id);
          if (response.ok) {
            const { articles } = get();
            const filteredArticles = articles.filter(
              (article) => article._id !== id
            );
            set({
              articles: filteredArticles,
              selectedArticle: null,
              isDeleting: false,
            });
            showToast.success("Xóa bài viết thành công");
          } else {
            set({
              error: response.data?.message || "Lỗi khi xóa bài viết",
              isDeleting: false,
            });
            showToast.error(response.data?.message || "Lỗi khi xóa bài viết");
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi xóa bài viết",
            isDeleting: false,
          });
          showToast.error(error.message || "Lỗi khi xóa bài viết");
        }
      },

      // Change article status
      changeStatus: async (id: string, status: string) => {
        set({ isUpdating: true, error: null });
        try {
          const response: any = await articleService.changeStatus(id, status);
          if (response.ok && response.data) {
            const { articles } = get();
            const updatedArticles = articles.map((article) =>
              article._id === id ? response.data : article
            );
            set({
              articles: updatedArticles,
              selectedArticle: response.data,
              isUpdating: false,
            });
            showToast.success("Thay đổi trạng thái thành công");
          } else {
            set({
              error: response.data?.message || "Lỗi khi thay đổi trạng thái",
              isUpdating: false,
            });
            showToast.error(response.data?.message || "Lỗi khi thay đổi trạng thái");
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi thay đổi trạng thái",
            isUpdating: false,
          });
          showToast.error(error.message || "Lỗi khi thay đổi trạng thái");
        }
      },

      // Toggle article display
      toggleDisplay: async (id: string) => {
        set({ isUpdating: true, error: null });
        try {
          const response: any = await articleService.toggleDisplay(id);
          if (response.ok && response.data) {
            const { articles } = get();
            const updatedArticles = articles.map((article) =>
              article._id === id ? response.data : article
            );
            set({
              articles: updatedArticles,
              selectedArticle: response.data,
              isUpdating: false,
            });
            showToast.success("Thay đổi hiển thị thành công");
          } else {
            set({
              error: response.data?.message || "Lỗi khi thay đổi hiển thị",
              isUpdating: false,
            });
            showToast.error(response.data?.message || "Lỗi khi thay đổi hiển thị");
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi thay đổi hiển thị",
            isUpdating: false,
          });
          showToast.error(error.message || "Lỗi khi thay đổi hiển thị");
        }
      },

      // Set selected article
      setSelectedArticle: (article: Article | null) => {
        set({ selectedArticle: article });
      },

      // Clear selected article
      clearSelectedArticle: () => {
        set({ selectedArticle: null });
      },

      // Clear error
      clearError: () => {
        set({ error: null });
      },

      // Set current page
      setCurrentPage: (page: number) => {
        set((state) => ({
          pagination: { ...state.pagination, page },
        }));
      },
    }),
    {
      name: "article-storage",
      partialize: (state) => ({
        pagination: state.pagination,
      }),
    }
  )
);
